<template>
    <v-container fluid class="ul-page">
        <v-breadcrumbs :items="breadcrumbsItems" class="pa-0 mb-2" large></v-breadcrumbs>

        <!-- 手機提醒：只看寬度，iPad 不會看到（spec §10.2） -->
        <v-alert v-if="showNarrowNotice" type="info" text dense dismissible
            class="mb-3" @input="dismissNarrowNotice">
            <div class="font-weight-medium">{{ $t('unit.narrow_notice.title') }}</div>
            <div class="text-caption">{{ $t('unit.narrow_notice.body') }}</div>
        </v-alert>

        <div class="ul-head">
            <h1 class="ul-h1">{{ $t('unit.list.page_title') }}</h1>
            <span class="ul-sum">
                {{ $t('unit.list.summary', { u: unitList.length, q: totalQuestCount }) }}
            </span>
        </div>

        <v-overlay :value="isLoading" absolute opacity="0.1">
            <v-progress-circular indeterminate size="48" color="primary"></v-progress-circular>
        </v-overlay>

        <v-card v-if="loadFailed" outlined class="pa-6 text-center">
            <v-icon size="48" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
            <div class="mt-3 mb-4 grey--text text--darken-1">
                {{ $t('unit.list.load_failed') }}
            </div>
            <v-btn color="primary" outlined class="normal-case" @click="load">
                {{ $t('unit.list.reload') }}
            </v-btn>
        </v-card>

        <template v-else-if="!isLoading">
            <!--
                順序待儲存提示列。重排**不會**立即送出——這是需求方特別要求的機制，
                改版設計稿寫「放下即生效」但刻意不採用（關卡順序動到學生進度）。
            -->
            <!--
                用 color + text 而不是 type="warning"：type 會讓 v-alert 進入 dark 情境，
                裡面的按鈕文字變白、在淺色底上看不見。
            -->
            <v-alert v-if="hasPendingOrder" color="warning" text dense class="mb-3">
                <div class="d-flex align-center flex-wrap">
                    <v-icon small color="warning" class="mr-2">mdi-alert</v-icon>
                    <span class="grey--text text--darken-3">
                        {{ $t('unit.list.order_pending') }}
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn text small color="grey darken-3" class="normal-case"
                        :disabled="isBusy" @click="discardOrder">
                        {{ $t('unit.list.discard_order') }}
                    </v-btn>
                    <v-btn color="primary" small depressed class="normal-case ml-2"
                        :loading="isReordering" @click="saveOrder">
                        {{ $t('unit.list.save_order') }}
                    </v-btn>
                </div>
            </v-alert>

            <div class="ul-body" data-rwd="body">
                <!-- 左：關卡清單 -->
                <div>
                    <div class="ul-hint">{{ $t('unit.list.drag_hint') }}</div>

                    <div v-if="unitList.length === 0" class="ul-empty">
                        {{ $t('unit.list.empty') }}
                    </div>

                    <draggable v-model="unitList" class="ul-cards"
                        :delay="150" :delay-on-touch-only="true"
                        handle=".uc-grip" :disabled="isBusy">
                        <div v-for="(item, index) in unitList" :key="item.unit"
                            class="uc"
                            :class="{ 'uc--on': hoverIndex === index, 'uc--empty': isEmpty(item) }"
                            @click="openUnit(index)"
                            @mouseenter="hoverIndex = index"
                            @mouseleave="hoverIndex = -1">
                            <span class="uc-grip" @click.stop>⠿</span>
                            <div class="uc-body">
                                <span class="uc-no">{{ index + 1 }}</span>
                                <div class="uc-text">
                                    <div class="uc-name" :class="{ 'is-empty': !item.name }">
                                        {{ item.name || $t('unit.list.unnamed') }}
                                    </div>
                                    <!-- ⚠ 空關卡要**醒目**，不能只是一行灰字。
                                         「新增關卡」不再順手建題目（2026-08-28），所以
                                         每一個新關卡都會先經過這個狀態——老師必須一眼
                                         看出「這一關還沒東西」，否則會以為已經建好了。 -->
                                    <div v-if="isEmpty(item)" class="uc-warn">
                                        {{ $t('unit.list.no_quest_warn') }}
                                    </div>
                                    <div v-else class="uc-meta">{{ questMeta(item) }}</div>
                                </div>
                            </div>
                            <span class="uc-go">›</span>
                        </div>
                    </draggable>

                    <button type="button" class="ul-add"
                        :disabled="isFull || isBusy" @click="onAddClick">
                        {{ isFull ? $t('unit.list.max_tooltip', { n: maxUnits })
                            : $t('unit.list.add') }}
                    </button>
                    <!-- 按鈕的行為在 2026-08-28 變了（不再順手建題目），
                         所以要一行說明接下來會發生什麼 -->
                    <div v-if="!isFull" class="ul-add-hint">{{ $t('unit.list.add_hint') }}</div>
                </div>

                <!-- 右：學生地圖預覽 -->
                <div class="ul-map">
                    <div class="mp-head">
                        <span class="mp-title">{{ $t('unit.list.map_title') }}</span>
                        <span v-if="hoverIndex >= 0" class="mp-sub">
                            {{ $t('unit.list.map_current', { n: hoverIndex + 1 }) }}
                        </span>
                    </div>

                    <div class="mp-stage" :style="stageStyle">
                        <img v-if="map.background_url" class="mp-bg"
                            :src="map.background_url" alt="">
                        <!--
                            終點圖示。座標與尺寸都跟著底圖等比縮放，跟學生端一致
                            （他們是 native size × scaleRatio），所以寬度用百分比而不是 px。
                        -->
                        <img v-if="goalStyle" class="mp-goal" :src="GOAL_ICON"
                            :style="goalStyle" alt="">
                        <!--
                            節點依**位置**取座標，不是依 unit 編號——拖曳時關卡換位置，
                            節點位置不變、只有編號跟著走，所以拖曳不必重打 API。
                            關卡「數量」變了才要重抓（座標整組不同）。
                        -->
                        <span v-for="(item, index) in placedUnits" :key="item.unit"
                            class="mp-node"
                            :class="{
                                'mp-node--todo': (item.quests || []).length === 0,
                                'mp-node--on': hoverIndex === index,
                            }"
                            :style="nodeStyle(index)">
                            {{ index + 1 }}
                        </span>
                    </div>

                    <div v-if="mapFailed" class="mp-note mp-note--warn">
                        {{ $t('unit.list.map_failed') }}
                    </div>
                    <div v-else-if="unitList.length && !map.nodes.length" class="mp-note mp-note--warn">
                        {{ $t('unit.list.map_empty') }}
                    </div>
                    <div v-else class="mp-note">{{ $t('unit.list.map_note') }}</div>
                </div>
            </div>
        </template>

        <!-- 順序未儲存時，其他動作一律先攔下 -->
        <v-dialog v-model="orderDialog" persistent width="520">
            <v-card>
                <v-card-title class="text-h6">
                    {{ $t('unit.list.order_confirm_title') }}
                </v-card-title>
                <v-card-text>{{ $t('unit.list.order_confirm_message') }}</v-card-text>
                <v-card-actions class="flex-wrap">
                    <v-spacer></v-spacer>
                    <v-btn text class="normal-case" :disabled="isReordering"
                        @click="resolvePending(false)">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn text class="normal-case" :disabled="isReordering"
                        @click="resolvePending('discard')">
                        {{ $t('unit.list.discard_and_continue') }}
                    </v-btn>
                    <v-btn color="primary" class="normal-case" :loading="isReordering"
                        @click="resolvePending('save')">
                        {{ $t('unit.list.save_and_continue') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <SnackMsg ref="snack" />
    </v-container>
</template>

<script>
import draggable from 'vuedraggable';
import { templateUnitServices } from '@/services/templateUnit.js';
import { diaryServices } from '@/services/diary.js';
import SnackMsg from '@/components/console/diary/component/SnackMsg';

/// 老師沒設過 `count` 時的預設。⚠ 必須與 `UnitEdit` 的 `FREE_COUNT_DEFAULT` 以及
/// 學生端的 `count || 3` 一致——三處不同的話，清單上的數字會與學生實際能寫的不符。
const FREE_COUNT_DEFAULT = 3;

/// 從學生端複製過來的同一張圖（studio-web/public/assets/stage/goal.png，151×152）
const GOAL_ICON = require('@/assets/map/goal.png');
/// 圖檔的原始寬度，用來換算成底圖座標系的百分比
const GOAL_NATIVE_WIDTH = 151;

const NARROW_NOTICE_KEY = 'unit_narrow_notice_dismissed';

/**
 * 關卡頁（設計稿 1a）：左邊關卡清單，右邊學生地圖預覽。
 *
 * 刪除關卡**不在這一頁**——設計稿把卡片做成整張可點的連結，卡片內沒有其他按鈕，
 * 所以刪除入口移到關卡頁（`UnitEdit`）的關卡設定卡裡。
 *
 * 重排維持「拖完要按儲存」的機制（需求方 2026-08-07 要求）。設計稿寫的是
 * 「放下即生效」，**刻意不採用**：關卡順序會動到學生已完成的進度。
 */
export default {
    name: 'UnitList',
    components: { draggable, SnackMsg },
    data() {
        return {
            homeworkId: '',
            homeworkName: '',
            breadcrumbsItems: [],
            isLoading: true,
            loadFailed: false,
            isAdding: false,
            isReordering: false,
            unitList: [],
            /// 上次與後端一致的順序，用來判斷有沒有未儲存的變更
            serverUnitList: [],
            maxUnits: 0,
            map: { background_url: '', goal: null, width: 0, height: 0, nodes: [] },
            GOAL_ICON: GOAL_ICON,
            mapFailed: false,
            hoverIndex: -1,
            orderDialog: false,
            pendingResolve: null,
            narrowNoticeDismissed: false,
        };
    },
    computed: {
        showNarrowNotice() {
            return this.$vuetify.breakpoint.xs && !this.narrowNoticeDismissed;
        },
        isFull() {
            return this.maxUnits > 0 && this.unitList.length >= this.maxUnits;
        },
        isBusy() {
            return this.isAdding || this.isReordering;
        },
        hasPendingOrder() {
            if (this.unitList.length !== this.serverUnitList.length) {
                return false;
            }
            return this.unitList.some(
                (item, index) => item.unit !== this.serverUnitList[index].unit);
        },
        totalQuestCount() {
            return this.unitList.reduce((n, item) => n + (item.quests || []).length, 0);
        },
        /// 只畫得出座標的那幾關。座標組比關卡少時，多出來的關卡不畫節點。
        placedUnits() {
            return this.unitList.slice(0, this.map.nodes.length);
        },
        /**
         * 終點圖示的位置與大小。座標同樣是**底圖像素、左上角錨點**。
         * 寬度換算成百分比，這樣縮放地圖時圖示會跟著等比縮小，與學生端一致。
         */
        goalStyle() {
            const goal = this.map.goal;
            const { width, height } = this.map;
            if (!goal || width <= 0 || height <= 0) {
                return null;
            }
            return {
                left: `${(goal.x / width) * 100}%`,
                top: `${(goal.y / height) * 100}%`,
                width: `${(GOAL_NATIVE_WIDTH / width) * 100}%`,
            };
        },
        /// 底圖是近正方形（實測 1960×1965），比例一律讀 API，不寫死
        stageStyle() {
            const { width, height } = this.map;
            return width > 0 && height > 0
                ? { aspectRatio: `${width} / ${height}` }
                : { aspectRatio: '1 / 1' };
        },
    },
    async created() {
        this.homeworkId = this.$route.params.id;
        this.narrowNoticeDismissed = sessionStorage.getItem(NARROW_NOTICE_KEY) === '1';
        await this.load();
    },
    mounted() {
        window.addEventListener('beforeunload', this.onBeforeUnload);
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
    },
    beforeRouteLeave(to, from, next) {
        if (!this.hasPendingOrder) {
            next();
            return;
        }
        const proceed = () => next();
        proceed.onCancel = () => next(false);
        this.guardPendingOrder(proceed);
    },
    methods: {
        async load() {
            this.isLoading = true;
            this.loadFailed = false;
            try {
                const [detailRes, listRes] = await Promise.all([
                    diaryServices.getHomeworkDetail(this.homeworkId),
                    templateUnitServices.getUnitList(this.homeworkId),
                ]);
                this.homeworkName = detailRes?.data?.name || '';
                this.maxUnits = listRes?.data?.max_units || 0;
                this.unitList = listRes?.data?.units || [];
                this.serverUnitList = this.unitList.slice();
                this.buildBreadcrumbs();
            } catch (err) {
                /// 呼叫端自己 catch —— service 只 throw（spec §9）
                this.loadFailed = true;
            } finally {
                this.isLoading = false;
            }
            await this.loadMap();
        },

        /**
         * 地圖預覽。**關卡數量變了才需要重抓**——`map.units` 是每一種關卡總數各有
         * 一組完全不同的座標，數量不變時拖曳只是換編號，位置不動。
         */
        async loadMap() {
            this.mapFailed = false;
            try {
                const res = await templateUnitServices.getMapPreview(this.homeworkId);
                this.map = {
                    background_url: res?.data?.background_url || '',
                    goal: res?.data?.goal || null,
                    width: res?.data?.width || 0,
                    height: res?.data?.height || 0,
                    nodes: res?.data?.nodes || [],
                };
            } catch (err) {
                this.mapFailed = true;
                this.map = { background_url: '', goal: null, width: 0, height: 0, nodes: [] };
            }
        },

        /// 只重抓列表與地圖，不動 breadcrumb 與整頁 loading（操作後的刷新用）
        async refresh() {
            try {
                const listRes = await templateUnitServices.getUnitList(this.homeworkId);
                this.maxUnits = listRes?.data?.max_units || 0;
                this.unitList = listRes?.data?.units || [];
                this.serverUnitList = this.unitList.slice();
            } catch (err) {
                this.toastError(this.$t('unit.list.load_failed'));
            }
            await this.loadMap();
        },

        buildBreadcrumbs() {
            this.breadcrumbsItems = [{
                text: this.$t('main_menu.quest'),
                disabled: false,
                href: '/diarySample',
            }, {
                text: this.homeworkName,
                disabled: false,
                /// 範本內容主頁（舊版與關卡式共用），不是 /diarySample/:id
                href: `/diary/homework/${this.homeworkId}/quest`,
            }, {
                text: this.$t('unit.list.page_title'),
                disabled: true,
            }];
        },

        /**
         * 這一關還沒有東西可做。
         *
         * ⚠ 用 quests 陣列而不是 quest_count——後端補回來的空關卡兩者都有，但陣列一定存在。
         *
         * ⚠⚠ **開了自由撰寫的關卡即使 0 題也不算空。** 學生會在那裡寫自己的篇數，
         * 那是刻意不出題的設計；標成「尚無題目」的醒目警告會叫老師去修一個沒壞的東西。
         */
        isEmpty(item) {
            if (item.free_creation === true) {
                return false;
            }
            return (item.quests || []).length === 0;
        },

        /**
         * 自由撰寫時學生還能寫幾篇。
         *
         * ⚠ `free_count` 是那一關的**總篇數**（含老師出的題目），所以要減掉題目數。
         * ⚠ `null` = 資料裡沒有 `count` 那個 key，學生端當 3（`count || 3`），
         * 這裡要用同一個預設，否則清單上的數字與學生實際能寫的不一樣。
         */
        freeRemaining(item) {
            const cap = typeof item.free_count === 'number'
                ? item.free_count : FREE_COUNT_DEFAULT;
            return Math.max(0, cap - (item.quests || []).length);
        },

        questMeta(item) {
            const n = (item.quests || []).length;
            const parts = [];
            if (n > 0) {
                parts.push(this.$t('unit.list.quest_n', { n }));
            }
            if (item.free_creation === true) {
                parts.push(this.$t('unit.list.free_max', { n: this.freeRemaining(item) }));
            }
            /// 兩者都沒有 = 真的空關卡（自由撰寫沒開、也沒題目）。
            /// ⚠ 這個分支實際上到不了：`isEmpty` 那時是 true，template 會走警告那一支。
            /// 留著是為了這支函式單獨被呼叫時也有合理回傳。
            if (parts.length === 0) {
                return this.$t('unit.list.no_quest');
            }
            return parts.join(' · ');
        },

        /**
         * 節點位置換算成百分比。
         *
         * ⚠ 座標的基準是**底圖像素**，而且是節點方形的**左上角**（與學生端一致，
         * 他們用 PixiJS，預設錨點就是左上角）。所以 CSS 那邊不能再 translate(-50%)。
         */
        nodeStyle(index) {
            const node = this.map.nodes[index];
            const { width, height } = this.map;
            if (!node || width <= 0 || height <= 0) {
                return { display: 'none' };
            }
            return {
                left: `${(node.x / width) * 100}%`,
                top: `${(node.y / height) * 100}%`,
            };
        },

        openUnit(index) {
            if (this.isBusy) {
                return;
            }
            const item = this.unitList[index];
            this.guardPendingOrder(() => {
                this.$router.push(`/diarySample/${this.homeworkId}/units/${item.unit}`);
            });
        },

        onAddClick() {
            if (this.isFull || this.isBusy) {
                return;
            }
            this.guardPendingOrder(() => this.doAdd());
        },

        /**
         * ⚠ **只建關卡，不建題目也不問模式**（2026-08-28 改）。
         *
         * 原本這裡會連帶建該關第一題，所以必須先問「第一題要做什麼模式」——
         * 但老師按「新增關卡」的當下想的是「我要多一關」，還沒想到第一題。
         * 而模式**建立後不可變更**（ADR 0002），逼他在那個時機決定是最糟的時機。
         *
         * 模式改由關卡內的「新增題目」問。新關卡會是空的，卡片上有醒目提示。
         */
        async doAdd() {
            this.isAdding = true;
            try {
                await templateUnitServices.addUnit(this.homeworkId);
                /// 新增後**不自動導向**關卡頁：老師常常連續加好幾關
                await this.refresh();
            } catch (err) {
                this.toastError(this.$t('unit.list.add_failed'));
            } finally {
                this.isAdding = false;
            }
        },

        /**
         * 有未儲存的順序時，任何其他動作都要先問過。
         * @param {Function} action 確認後要執行的動作
         */
        guardPendingOrder(action) {
            if (!this.hasPendingOrder) {
                action();
                return;
            }
            this.pendingResolve = action;
            this.orderDialog = true;
        },

        /**
         * 對話框的三個出口。
         * @param {'save'|'discard'|false} choice
         */
        async resolvePending(choice) {
            if (choice === false) {
                this.orderDialog = false;
                const action = this.pendingResolve;
                this.pendingResolve = null;
                /// 離開頁面的攔截需要知道使用者取消了，才能中止導航
                if (action && action.onCancel) {
                    action.onCancel();
                }
                return;
            }
            if (choice === 'save') {
                const ok = await this.saveOrder();
                if (!ok) {
                    /// 儲存失敗就停在原地，不執行後續動作也不關對話框
                    return;
                }
            } else {
                this.discardOrder();
            }
            this.orderDialog = false;
            const action = this.pendingResolve;
            this.pendingResolve = null;
            if (action) {
                action();
            }
        },

        /** 把目前順序寫進資料庫。回傳是否成功。 */
        async saveOrder() {
            if (!this.hasPendingOrder) {
                return true;
            }
            this.isReordering = true;
            try {
                /// ⚠ 送的是 unit 編號的新順序，不是 quest_id
                await templateUnitServices.reorderUnits(
                    this.homeworkId, this.unitList.map((item) => item.unit));
                await this.refresh();
                this.$refs.snack?.showAlertMsg('success', this.$t('unit.list.order_saved'));
                return true;
            } catch (err) {
                /// 失敗時**不還原**：老師拖了半天的順序不該因為一次網路失敗就消失，
                /// 提示列還在，可以再按一次儲存。
                this.toastError(this.$t('unit.list.reorder_failed'));
                return false;
            } finally {
                this.isReordering = false;
            }
        },

        /** 丟棄未儲存的順序，回到後端的順序 */
        discardOrder() {
            this.unitList = this.serverUnitList.slice();
        },

        /**
         * 關閉分頁／重新整理的攔截。瀏覽器只會顯示原生訊息，文案無法自訂。
         * 注意 breadcrumb 用的是 `href`（整頁導航），會走這裡而不是 beforeRouteLeave。
         */
        onBeforeUnload(event) {
            if (!this.hasPendingOrder) {
                return;
            }
            event.preventDefault();
            event.returnValue = '';
        },

        toastError(msg) {
            this.$toastr.Add({
                msg: msg,
                type: 'error',
                timeout: 4000,
                clickClose: true,
                position: 'toast-top-right',
                classNames: ['animated', 'bounceInRight'],
            });
        },

        dismissNarrowNotice() {
            this.narrowNoticeDismissed = true;
            sessionStorage.setItem(NARROW_NOTICE_KEY, '1');
        },
    },
};
</script>

<style scoped>
/* 設計 token 見 support/關卡編輯/介面調整/README.md。方角、細陰影、不用重點色當狀態。 */
.ul-page {
    padding-bottom: 40px;
}

.ul-head {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 16px;
}

.ul-h1 {
    font-size: 26px;
    font-weight: 500;
    color: #424242;
    line-height: 1.3;
    margin: 0;
}

.ul-sum {
    font-size: 13px;
    color: #9e9e9e;
    margin-left: auto;
}

.ul-body {
    display: grid;
    grid-template-columns: 456px 1fr;
    gap: 24px;
    align-items: start;
}

.ul-hint {
    font-size: 12px;
    color: #9e9e9e;
    margin-bottom: 10px;
}

.ul-add-hint {
    font-size: 12px;
    color: #90a4ae;
    line-height: 1.5;
    margin-top: 6px;
}

.uc--empty {
    /* 空關卡：左邊一條橘色的邊，掃一眼就看得出來哪幾關還沒東西 */
    border-left: 3px solid #fb8c00;
}

.uc-warn {
    font-size: 12px;
    color: #e65100;
    font-weight: 600;
    margin-top: 2px;
}

.ul-empty {
    font-size: 13px;
    color: #757575;
    margin-bottom: 10px;
}

.ul-cards {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

/* 整張卡片可點；卡片內沒有其他按鈕（刪除移到關卡頁） */
.uc {
    display: flex;
    align-items: stretch;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .14);
    cursor: pointer;
}

/* 選取／hover 用中性灰，不用重點色——重點色在這個設計裡只給操作 */
.uc--on {
    outline: 1px solid #9e9e9e;
    box-shadow: 0 2px 8px rgba(0, 0, 0, .18);
}

.uc-grip {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    flex: 0 0 32px;
    color: #bdbdbd;
    font-size: 15px;
    cursor: grab;
}

.uc-body {
    flex: 1;
    min-width: 0;
    padding: 12px 14px;
    /* 序號自成一欄，標題與題數在右欄堆疊 —— 題數才會對齊標題而不是卡片左緣。
       用 baseline 對齊，序號 12px 與標題 16px 的字才不會一高一低。
       序號寬度隨位數變（1 → 10），所以不能用固定縮排代替。 */
    display: flex;
    align-items: baseline;
    gap: 8px;
}

.uc-no {
    font-size: 12px;
    color: #bdbdbd;
    flex: 0 0 auto;
}

.uc-text {
    flex: 1;
    min-width: 0;
}

.uc-name {
    font-size: 16px;
    font-weight: 500;
    color: #424242;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.uc-name.is-empty {
    color: #9e9e9e;
}

.uc-meta {
    font-size: 12px;
    color: #9e9e9e;
    margin-top: 8px;
}

.uc-go {
    display: flex;
    align-items: center;
    padding: 0 14px;
    color: #bdbdbd;
    font-size: 20px;
}

.ul-add {
    display: block;
    width: 100%;
    margin-top: 10px;
    border: 1px dashed #c9c9c9;
    background: #fafafa;
    color: #9e9e9e;
    font-size: 14px;
    padding: 16px;
    text-align: center;
    cursor: pointer;
}

.ul-add:hover:not(:disabled) {
    border-color: #9e9e9e;
    color: #757575;
}

.ul-add:disabled {
    cursor: not-allowed;
    color: #bdbdbd;
}

.ul-map {
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .14);
    position: sticky;
    top: 74px;
}

.mp-head {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #eeeeee;
}

.mp-title {
    font-size: 15px;
    font-weight: 500;
    color: #424242;
}

.mp-sub {
    font-size: 12px;
    color: #9e9e9e;
    margin-left: auto;
}

.mp-stage {
    position: relative;
    width: 100%;
    overflow: hidden;
    background: #eceff1;
}

.mp-bg {
    position: absolute;
    inset: 0 auto auto 0;
    width: 100%;
    display: block;
}

/* 終點圖示。壓在底圖上、節點下方——關卡節點才是老師要操作的東西。 */
.mp-goal {
    position: absolute;
    display: block;
    height: auto;
    pointer-events: none;
}

/* 節點上不顯示關卡名稱——學生端也不顯示 */
.mp-node {
    position: absolute;
    /* ⚠ 資料表的座標是節點方形的**左上角**，不是中心點，
       所以這裡不能用 translate(-50%, -50%) 把它當圓心。 */
    /* 外徑（含白框）。line-height 必須等於「外徑 − 2×border」，
       否則數字不會落在圓心。 */
    width: 27.5px;
    height: 27.5px;
    border-radius: 50%;
    background: #37474f;
    border: 3px solid #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, .28);
    color: #fff;
    font-size: 11.5px;
    font-weight: 700;
    line-height: 21.5px;
    text-align: center;
}

.mp-node--todo {
    background: rgba(120, 120, 120, .85);
    border-style: dashed;
}

.mp-node--on {
    /* 維持設計稿的 1.28 倍放大比例（原本 36 → 46）。
       負 margin 讓它從中心往外長，否則左上角固定會看起來像往右下滑掉。 */
    width: 35px;
    height: 35px;
    margin: -3.75px 0 0 -3.75px;
    border-width: 4px;
    font-size: 14.5px;
    line-height: 27px;
    box-shadow: 0 0 0 5px rgba(255, 255, 255, .55), 0 3px 8px rgba(0, 0, 0, .3);
    z-index: 2;
}

.mp-note {
    font-size: 12px;
    color: #9e9e9e;
    line-height: 1.6;
    border-top: 1px solid #eeeeee;
    padding: 12px 16px;
}

.mp-note--warn {
    color: #e65100;
}

/* 兩欄放不下時改成上下堆疊；地圖不再 sticky */
@media (max-width: 1179px) {
    [data-rwd="body"] {
        grid-template-columns: 1fr;
    }

    /* 卡片本身佔滿整欄（標題列與說明文字才不會縮在左邊一小塊），
       但底圖近正方形，撐滿寬度會變成一大塊，所以**只把地圖本身限寬並置中**。 */
    .ul-map {
        position: static;
    }

    .mp-stage {
        max-width: 520px;
        margin: 0 auto;
    }
}
</style>
